import React, { createContext, useState, useCallback, ReactNode } from 'react';
import { GuideConfig, GuideState } from './types';

interface GuideContextValue {
  state: GuideState;
  startGuide: (config: GuideConfig) => void;
  nextStep: () => void;
  previousStep: () => void;
  skipGuide: () => void;
  completeGuide: () => void;
  getCurrentStep: () => GuideConfig['steps'][0] | null;
  isCurrentStep: (stepId: string) => boolean;
  config: GuideConfig | null;
}

export const GuideContext = createContext<GuideContextValue | null>(null);

interface GuideProviderProps {
  children: ReactNode;
  onGuideSkipped?: () => void;
  onGuideCompleted?: () => void;
}

export const GuideProvider: React.FC<GuideProviderProps> = ({
  children,
  onGuideSkipped,
  onGuideCompleted,
}) => {
  const [state, setState] = useState<GuideState>({
    isActive: false,
    currentStepIndex: 0,
    guideId: null,
  });

  const [config, setConfig] = useState<GuideConfig | null>(null);

  const completeGuide = useCallback(() => {
    if (!config) return;

    config.analytics?.trackGuideCompleted?.(config.id);

    onGuideCompleted?.();

    setState({
      isActive: false,
      currentStepIndex: 0,
      guideId: null,
    });
    setConfig(null);
  }, [config, onGuideCompleted]);

  const startGuide = useCallback((guideConfig: GuideConfig) => {
    setConfig(guideConfig);
    setState({
      isActive: true,
      currentStepIndex: 0,
      guideId: guideConfig.id,
    });

    guideConfig.analytics?.trackGuideStarted?.(guideConfig.id);

    if (guideConfig.steps.length > 0) {
      const firstStep = guideConfig.steps[0];
      guideConfig.analytics?.trackStepViewed?.(firstStep.id, firstStep.title);
    }
  }, []);

  const nextStep = useCallback(() => {
    if (!config || !state.isActive) return;

    const currentStep = config.steps[state.currentStepIndex];
    if (currentStep) {
      config.analytics?.trackStepCompleted?.(currentStep.id, currentStep.title);
    }

    const nextIndex = state.currentStepIndex + 1;

    if (nextIndex >= config.steps.length) {
      completeGuide();
      return;
    }

    setState((prev: GuideState) => ({ ...prev, currentStepIndex: nextIndex }));

    const nextStep = config.steps[nextIndex];
    if (nextStep) {
      config.analytics?.trackStepViewed?.(nextStep.id, nextStep.title);
    }
  }, [config, state, completeGuide]);

  const previousStep = useCallback(() => {
    if (!state.isActive || state.currentStepIndex === 0) return;

    setState((prev: GuideState) => ({
      ...prev,
      currentStepIndex: prev.currentStepIndex - 1,
    }));
  }, [state]);

  const skipGuide = useCallback(() => {
    if (!config || !state.isActive) return;

    config.analytics?.trackGuideSkipped?.(
      config.id,
      state.currentStepIndex + 1,
      config.steps.length,
    );

    onGuideSkipped?.();

    setState({
      isActive: false,
      currentStepIndex: 0,
      guideId: null,
    });
    setConfig(null);
  }, [config, state, onGuideSkipped]);

  const getCurrentStep = useCallback(() => {
    if (!config || !state.isActive) return null;
    return config.steps[state.currentStepIndex] || null;
  }, [config, state]);

  const isCurrentStep = useCallback(
    (stepId: string) => {
      const currentStep = getCurrentStep();
      return currentStep?.id === stepId;
    },
    [getCurrentStep],
  );

  const value: GuideContextValue = {
    state,
    startGuide,
    nextStep,
    previousStep,
    skipGuide,
    completeGuide,
    getCurrentStep,
    isCurrentStep,
    config,
  };

  return (
    <GuideContext.Provider value={value}>{children}</GuideContext.Provider>
  );
};
